import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Typography,
  Button,
  Textarea,
  Box,
  Alert
} from '@mui/joy';
import ModalSheet from '../common/ModalSheet';
import WarningIcon from '@mui/icons-material/Warning';

interface SendCheckInModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (message: string) => Promise<void>;
  applicantName: string;
  applicantId: string;
}

const SendCheckInModal: React.FC<SendCheckInModalProps> = ({
  open,
  onClose,
  onSend,
  applicantName,
  applicantId
}) => {
  const currentHost = window.location.origin;
  
  const defaultMessage = `Hey ${applicantName}, it's Gemma from Geek App! I'm just checking in to see if you're still open to receiving meeting invites from potential employers.

If yes, click here: ${currentHost}/applicants/${applicantId}/confirm-active

If no, click here: ${currentHost}/applicants/${applicantId}/set-inactive`;

  const [message, setMessage] = useState(defaultMessage);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset message when modal opens
  React.useEffect(() => {
    if (open) {
      setMessage(defaultMessage);
      setError(null);
    }
  }, [open, defaultMessage]);

  const handleSend = async () => {
    if (!message.trim()) {
      setError('Message cannot be empty');
      return;
    }

    try {
      setIsSending(true);
      setError(null);
      await onSend(message);
      onClose();
    } catch (err: any) {
      console.error('Error sending check-in:', err);
      setError(err.message || 'Failed to send check-in text. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    setMessage(defaultMessage);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'none',
          },
        },
      }}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: ['flex-end', 'center'],
        p: 0,
        border: 'none',
      }}
    >
      <ModalSheet
        sx={{
          maxWidth: '600px',
          width: '100%',
          scrollbarWidth: 'none',
          paddingInline: '24px',
          paddingBottom: '24px',
        }}
      >
        <Stack direction="column" spacing={2}>
          <Box sx={{ py: 2, borderBottom: '1px solid #E5E7EB' }}>
            <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D' }}>
              Send Check-In Text
            </Typography>
            <Typography level="body-sm" sx={{ color: '#7B7F85', mt: 0.5 }}>
              Send a text message to {applicantName} to check if they're still active
            </Typography>
          </Box>

          {error && (
            <Alert 
              color="danger" 
              startDecorator={<WarningIcon />}
              sx={{ borderRadius: '8px' }}
            >
              {error}
            </Alert>
          )}

          <Stack spacing={1}>
            <Typography level="body-sm" sx={{ fontWeight: 500, color: '#1A1B1D' }}>
              Message
            </Typography>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              minRows={8}
              sx={{
                backgroundColor: '#ECEFF3',
                borderRadius: '12px',
                border: 'none',
                fontSize: '1rem',
                color: '#1A1B1D',
                '--Textarea-focusedHighlight': 'transparent !important',
                '&::placeholder': {
                  color: '#7B7F85 !important',
                  opacity: 1,
                },
                '&:hover': {
                  backgroundColor: '#ECEFF3',
                },
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  backgroundColor: '#ECEFF3',
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', pt: 2 }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={handleCancel}
              disabled={isSending}
              sx={{
                borderColor: '#D1D5DB',
                color: '#1A1B1D',
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={handleSend}
              loading={isSending}
              sx={{
                backgroundColor: '#2e2d7e',
                '&:hover': {
                  backgroundColor: '#1e1d6e',
                },
              }}
            >
              Send Check-In
            </Button>
          </Stack>
        </Stack>
      </ModalSheet>
    </Modal>
  );
};

export default SendCheckInModal;

